/* eslint-disable complexity */
import { IconNames } from '@blueprintjs/icons';
import { ActionButtonPageTitle, ActionButtonText, Button } from '@strata/react-buttons';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { GridWithFilters } from '@strata/react-grid';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { StatusIndicator } from '@strata/react-status-indicators';
import PropTypes from 'prop-types';

const renderStatusValue = (status) => {
    if (!status) {
        return undefined;
    }

    return (
        <StatusIndicator
            message={status.message}
            status={status.indicatorStatus ?? StatusIndicator.Status.DEFAULT}
        />
    );
};

export const OrtfOutboundFileScriptsLayout = ({
    activeFilters = undefined,
    backLabel = 'Outbound Files',
    createdDate = undefined,
    fileDate = undefined,
    filterCategories = undefined,
    filterPaneWidth = undefined,
    goBack,
    isDownloading = false,
    isFetching,
    isSending = false,
    kpiData = undefined,
    onDownloadFile = undefined,
    onSendFile = undefined,
    outboundFile = undefined,
    outboundFileStatus,
    payers = undefined,
    scriptColumns,
    scripts,
    showSendFile = true,
    sort = undefined,
    title = 'Outbound File Details',
    updateFilterPaneWidth,
    updateGridActiveFilters,
    updateGridSort,
}) => (
    <div className="sds_ortf-outbound-file-scripts">
        <FormHeaderAndContent
            rowOneContentHeader={
                <>
                    <ActionButtonPageTitle label={backLabel} onClick={goBack} title={title} />
                    <div className="flex__gap--medium" style={{ marginLeft: 'auto' }}>
                        <ActionButtonText
                            disabled={!onDownloadFile || isDownloading}
                            label="Download File"
                            leftIcon={IconNames.DOWNLOAD}
                            onClick={onDownloadFile}
                        />
                        {showSendFile ? (
                            <Button
                                disabled={!onSendFile || isSending}
                                intent={Button.Intent.PRIMARY}
                                label="Send File"
                                onClick={onSendFile}
                            />
                        ) : null}
                    </div>
                </>
            }
            rowTwoContentHeader={
                <>
                    <FormTitleLabelValuePair
                        label={outboundFile?.pharmacy ?? '--'}
                        value={fileDate}
                    />
                    <HeaderLabelValuePair label="Payers" value={payers} />
                    <HeaderLabelValuePair label="File Name" value={outboundFile?.fileName} />
                    <HeaderLabelValuePair label="Created Date" value={createdDate} />
                    <HeaderLabelValuePair
                        label="Status"
                        value={renderStatusValue(outboundFileStatus)}
                    />
                </>
            }
            mainContent={
                <GridWithFilters
                    activeFilters={activeFilters}
                    columns={scriptColumns}
                    data={scripts}
                    enableSearch
                    filterCategories={filterCategories}
                    filterPaneRenderWidth={filterPaneWidth}
                    isLoading={isFetching}
                    kpiData={kpiData}
                    onFilter={updateGridActiveFilters}
                    onFilterPaneResize={updateFilterPaneWidth}
                    onSort={updateGridSort}
                    sortProperties={sort}
                />
            }
        />
    </div>
);

const outboundFileStatusPropType = PropTypes.shape({
    indicatorStatus: PropTypes.string,
    message: PropTypes.string.isRequired,
});

OrtfOutboundFileScriptsLayout.propTypes = {
    activeFilters: PropTypes.shape({}),
    backLabel: PropTypes.string,
    createdDate: PropTypes.string,
    fileDate: PropTypes.string,
    filterCategories: PropTypes.arrayOf(PropTypes.shape({})),
    filterPaneWidth: PropTypes.number,
    goBack: PropTypes.func.isRequired,
    isDownloading: PropTypes.bool,
    isFetching: PropTypes.bool.isRequired,
    isSending: PropTypes.bool,
    kpiData: PropTypes.arrayOf(PropTypes.shape({})),
    onDownloadFile: PropTypes.func,
    onSendFile: PropTypes.func,
    outboundFile: PropTypes.shape({
        fileName: PropTypes.string,
        payer: PropTypes.string,
        pharmacy: PropTypes.string,
    }),
    outboundFileStatus: outboundFileStatusPropType.isRequired,
    payers: PropTypes.string,
    scriptColumns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    scripts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    showSendFile: PropTypes.bool,
    sort: PropTypes.arrayOf(
        PropTypes.shape({
            desc: PropTypes.bool,
            id: PropTypes.string.isRequired,
        }),
    ),
    title: PropTypes.string,
    updateFilterPaneWidth: PropTypes.func.isRequired,
    updateGridActiveFilters: PropTypes.func.isRequired,
    updateGridSort: PropTypes.func.isRequired,
};
